/**
 * useChoiceList
 *
 * Owns the three choice-list methods that were previously class methods on
 * FormViewer, called back from useFormFieldEvents via fieldEventsCallbacksRef:
 *
 *   openChoiceList(form, field)         — reset, fetch, and open the modal
 *   checkChoiceListForValue(fieldEl)    — check whether field value is in list
 *   handleChoiceListItem(choiceItem, f) — calc and close after selection
 *
 * Because checkChoiceListForValue reads Redux state (choiceListData /
 * choiceListFields) at call-time from jQuery event handlers, those values are
 * provided via a kept-current ref instead of useCallback deps.
 *
 * Returns:
 *   { openChoiceList, checkChoiceListForValue, handleChoiceListItem }
 */
import { useCallback } from 'react';

const useChoiceList = ({
  calcAPIRef,
  formNavRef, // kept-current ref to useFormNavigation result
  choiceListDataRef, // kept-current ref — { choiceListData, choiceListFields }
  // Redux action dispatchers
  resetChoiceList,
  toggleChoiceList,
}) => {
  const openChoiceList = useCallback(
    (form, field) => {
      resetChoiceList();
      calcAPIRef.current.ReturnChoiceList(form, field);
      toggleChoiceList(true);
    },
    [calcAPIRef, resetChoiceList, toggleChoiceList],
  );

  /**
   * Checks whether the current field value is present in the relevant choice-
   * list column. Returns true if the value is *missing* from the list.
   *
   * @param {HTMLElement} field  the current event.target html element
   * @returns {boolean}
   */
  const checkChoiceListForValue = useCallback(
    field => {
      const { choiceListData, choiceListFields } = choiceListDataRef.current;
      // slices field depending on type to retrieve column name
      // which is then used to find the correct choicelist column
      const colName = field.getAttribute('stmcode') ? field.id.slice(0, 2) : field.id.slice(0, 4);
      const colIndex = choiceListFields.indexOf(colName);

      if (colIndex === -1) {
        return false;
      }

      // returns true if none of the items match the field value
      return choiceListData.every(
        item => item.ListItem[colIndex].COL.toLowerCase() !== field.value.toLowerCase(),
      );
    },
    [choiceListDataRef],
  );

  const handleChoiceListItem = useCallback(
    (choiceItem, field) => {
      let tempString = '';
      choiceItem.forEach(data => {
        tempString = tempString + data.COL + '\t';
      });
      // last row — remove trailing tab
      tempString = tempString.slice(0, -1);
      calcAPIRef.current.ReturnChoiceListCalc(formNavRef.current.activeForm, field, tempString);
      toggleChoiceList(false);
    },
    [calcAPIRef, formNavRef, toggleChoiceList],
  );

  return {
    openChoiceList,
    checkChoiceListForValue,
    handleChoiceListItem,
  };
};

export default useChoiceList;
